import { Link } from "react-router-dom";
import { Check, Zap, ArrowRight } from "lucide-react";

const TIERS = [
  {
    name: "Single Tag",
    price: "$19",
    period: "one-time",
    blurb: "For a single car or bike. The minimal install.",
    cta: "Get my tag",
    highlight: false,
    features: [
      "1 SECQRE windshield QR sticker",
      "Lifetime masked-call relay",
      "iOS + Android app",
      "Unlimited scans",
      "Pause / transfer anytime",
      "Standard email support",
    ],
  },
  {
    name: "Family Pack",
    price: "$39",
    period: "one-time",
    blurb: "Most popular. Cover 2 cars + 1 bike, plus replacements.",
    cta: "Choose Family",
    highlight: true,
    features: [
      "3 SECQRE tags (mix car / bike)",
      "Everything in Single",
      "Emergency-contact channel",
      "Document vault (registration, insurance)",
      "Scan-location heatmap",
      "Priority email + chat support",
    ],
  },
  {
    name: "Business Pack",
    price: "$199",
    period: "starting at",
    blurb: "For fleets, dealerships and resellers. Co-branded.",
    cta: "Talk to sales",
    highlight: false,
    features: [
      "20 co-branded SECQRE tags",
      "Fleet dashboard + admin roles",
      "Vehicle in/out logs (CSV export)",
      "Boom-barrier API integration",
      "Custom QR landing page",
      "Dedicated account manager",
    ],
  },
];

export default function PricingTeaser({ full = false }) {
  return (
    <section data-testid="pricing-section" className="section-pad bg-[#F8F9FA] border-y border-[#E2E8F0]">
      <div className="container-secqre">
        <div className="max-w-2xl mb-12">
          <span className="overline">— Pricing</span>
          <h2 className="mt-4 font-heading font-bold text-4xl sm:text-5xl tracking-tight" data-testid="pricing-heading">
            Buy once. Stay private forever.
          </h2>
          <p className="mt-5 text-lg text-[#475569]">
            No subscriptions. No surprises. One sticker, a free app, a lifetime of masked calls.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {TIERS.map((t, i) => (
            <div
              key={i}
              data-testid={`pricing-tier-${i}`}
              className={`relative rounded-2xl p-8 md:p-9 transition-all hover:-translate-y-1 ${
                t.highlight
                  ? "bg-[#0F172A] text-white border-2 border-[#0F172A]"
                  : "bg-white border border-[#E2E8F0]"
              }`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-7 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FFD600] text-[#0A0A0A] text-xs font-bold">
                  <Zap className="w-3.5 h-3.5" /> MOST POPULAR
                </div>
              )}
              <h3 className="font-heading font-bold text-2xl">{t.name}</h3>
              <p className={`mt-2 text-sm ${t.highlight ? "text-white/70" : "text-[#475569]"}`}>{t.blurb}</p>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-heading font-black text-5xl tracking-tighter">{t.price}</span>
                <span className={`text-sm ${t.highlight ? "text-white/60" : "text-[#475569]"}`}>{t.period}</span>
              </div>
              <ul className="mt-7 space-y-3">
                {t.features.map((f, k) => (
                  <li key={k} className="flex items-start gap-3 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.highlight ? "text-[#FFD600]" : "text-[#0F172A]"}`} />
                    <span className={t.highlight ? "text-white/90" : "text-[#0A0A0A]"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={t.name === "Business Pack" ? "/contact" : "/contact"}
                className={`mt-8 w-full ${t.highlight ? "btn-secondary" : "btn-primary"}`}
                data-testid={`pricing-cta-${i}`}
              >
                {t.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>

        {full && (
          <p className="mt-12 text-center text-sm text-[#475569]">
            All plans include a 60-day money-back guarantee. US shipping is free on orders over $25.
          </p>
        )}
      </div>
    </section>
  );
}
